import { useState, useEffect, useMemo } from 'react';
import { Container, Title, Text, Button, Stack, Select, Box, Card, Group } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeSelector } from '@/features/story/components/ThemeSelector';
import { ThemeLoadingAnimation } from '@/features/story/components/ThemeLoadingAnimation';
import { StoryGeneratingModal } from '@/features/story/components/StoryGeneratingModal';
import { useGenerateStory, useThemes, useThemeCategories } from '@/features/story/hooks/useStory';
import { useJobPolling } from '@/features/story/hooks/useJobPolling';
import { usePendingStories } from '@/features/story/hooks/usePendingStories';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useChildren } from '@/features/parent/hooks/useParent';
import type { StoryGenerationJobResponse } from '@/lib/api-client';

// Language options for story generation
const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English' },
  { value: 'ne-NP', label: 'Nepali (नेपाली)' },
];

const PAGE_TITLES = [
  "Spin a New Tale",
  "Start a Fresh Adventure",
  "Make Some Story Magic",
  "Let's Begin a New Chapter",
  "Tell Me Something New!",
  "Roll Out a New Story",
  "Imagine Again",
  "Craft Another Narrative",
  "Inspire a Fresh Plot",
  "Conjure the Next Tale",
  "Turn the Page",
  "Light Up a New Imagination",
  "Remix the Tale",
  "Next Story, Please!"
];

/**
 * Mobile Generate Story Page
 *
 * Mobile-optimized story generation with:
 * - Simple theme selection
 * - Language picker
 * - Touch-optimized UI
 */
export function MobileGenerateStoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isParent = user?.roles?.includes('parent');

  const [selectedTheme, setSelectedTheme] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedChildId, setSelectedChildId] = useState<string>('');
  const [languageCode, setLanguageCode] = useState<string>('en-US');
  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [waitingForStory, setWaitingForStory] = useState(false);

  const generateStory = useGenerateStory();
  const { data: categories } = useThemeCategories();
  const { data: themes, isLoading: isLoadingThemes } = useThemes({ categoryId: selectedCategory || undefined });
  const { addPending } = usePendingStories();
  const { data: children } = useChildren({ enabled: !!isParent });

  // Select a random title on component mount (stays consistent during session)
  const pageTitle = useMemo(() => {
    return PAGE_TITLES[Math.floor(Math.random() * PAGE_TITLES.length)];
  }, []);

  // Pre-select theme and child if coming from an assignment or quick read
  useEffect(() => {
    if (location.state?.themeId) {
      setSelectedTheme(location.state.themeId);
    }
    if (location.state?.forStudentId) {
      setSelectedChildId(location.state.forStudentId);
    }
  }, [location.state]);

  // Build child selector options
  const childOptions = useMemo(() => {
    if (!children) return [];
    return children.map((child) => ({
      value: child.id,
      label: child.display_name || 'Student',
    }));
  }, [children]);

  // Get selected theme details
  const selectedThemeData = useMemo(() => {
    if (!selectedTheme || !themes) return null;
    return themes.find(t => t.id === selectedTheme);
  }, [selectedTheme, themes]);

  // Poll for job completion only while on this page
  useJobPolling({
    jobId: currentJobId,
    enabled: currentJobId !== null && showModal,
    onComplete: (storyId) => {
      navigate(`/story/${storyId}`);
      setShowModal(false);
      setCurrentJobId(null);
      setWaitingForStory(false);
    },
    onError: () => {
      setShowModal(false);
      setCurrentJobId(null);
      setWaitingForStory(false);
    },
  });

  const handleGenerate = () => {
    if (!selectedTheme) return;

    // Use async generation for all users on mobile
    const useAsync = true;

    generateStory.mutate(
      {
        themeId: selectedTheme,
        languageCode,
        asyncGeneration: useAsync,
        forStudentId: selectedChildId || undefined,
      },
      {
        onSuccess: (result) => {
          if (useAsync && 'job_id' in result) {
            const jobResponse = result as StoryGenerationJobResponse;

            // Track pending story
            addPending({
              jobId: jobResponse.job_id,
              themeId: selectedTheme,
              themeName: selectedThemeData?.label || 'Unknown Theme',
              languageCode,
              submittedAt: new Date().toISOString(),
            });

            setCurrentJobId(jobResponse.job_id);
            setShowModal(true);
            setWaitingForStory(false);
          }
        },
      }
    );
  };

  const handleCreateAnother = () => {
    setShowModal(false);
    setWaitingForStory(false);
    setSelectedTheme('');
  };

  const handleGoToLibrary = () => {
    setShowModal(false);
    setWaitingForStory(false);
    navigate('/library');
  };

  const handleWaitForStory = () => {
    setWaitingForStory(true);
  };

  // Mobile always uses async generation, no need for loading animation
  // (loading state is handled by the modal)

  // Category filter options
  const categoryOptions = useMemo(() => {
    if (!categories) return [];
    return [
      { value: '', label: 'All Categories' },
      ...categories.map((cat) => ({
        value: cat.id,
        label: `${cat.emoji || ''} ${cat.name}`.trim(),
      })),
    ];
  }, [categories]);

  return (
    <>
      <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          <Stack gap="xs">
            <Title order={2}>{pageTitle}</Title>
            <Text size="sm" c="dimmed">
              Choose a theme and we'll create a personalized reading passage for you
            </Text>
          </Stack>
        </Box>

        {/* Content */}
        <Container size="sm" py="md" style={{ flex: 1, overflow: 'auto' }}>
          <Stack gap="lg">
            {selectedThemeData && (
              <Card withBorder p="sm" radius="md" style={{ borderColor: 'var(--mantine-color-teal-4)' }}>
                <Stack gap="xs">
                  <Group gap="sm">
                    <Text size="xl">{selectedThemeData.emoji || '📚'}</Text>
                    <Stack gap={2}>
                      <Text fw={600} size="sm">{selectedThemeData.label}</Text>
                      <Text size="xs" c="dimmed">Selected theme</Text>
                    </Stack>
                  </Group>
                  <Group gap="xs" grow>
                    {isParent && childOptions.length > 0 && (
                      <Select
                        size="xs"
                        placeholder="Select child"
                        data={childOptions}
                        value={selectedChildId}
                        onChange={(value) => setSelectedChildId(value || '')}
                        clearable
                      />
                    )}
                    <Select
                      size="xs"
                      data={LANGUAGE_OPTIONS}
                      value={languageCode}
                      onChange={(value) => setLanguageCode(value || 'en-US')}
                    />
                    <Button
                      variant="light"
                      size="sm"
                      loading={generateStory.isPending}
                      onClick={handleGenerate}
                    >
                      {generateStory.isPending ? 'Writing...' : 'Create Story'}
                    </Button>
                  </Group>
                </Stack>
              </Card>
            )}

            <Select
              label="Theme Category"
              placeholder="Filter by category"
              data={categoryOptions}
              value={selectedCategory}
              onChange={(value) => setSelectedCategory(value || '')}
              description="Filter themes by category (optional)"
              clearable
            />

            <ThemeSelector
              selectedTheme={selectedTheme}
              onSelectTheme={setSelectedTheme}
              themes={themes}
              isLoading={isLoadingThemes}
            />

            {!selectedThemeData && (
              <Select
                label="Story Language"
                placeholder="Select language"
                data={LANGUAGE_OPTIONS}
                value={languageCode}
                onChange={(value) => setLanguageCode(value || 'en-US')}
                description="Choose the language for your story"
              />
            )}

            {!selectedThemeData && (
              <Stack gap="xs">
                <Button
                  variant="light"
                  size="lg"
                  disabled={!selectedTheme}
                  loading={generateStory.isPending}
                  onClick={handleGenerate}
                  fullWidth
                >
                  {generateStory.isPending ? 'Writing Your Story...' : 'Create Story'}
                </Button>
              </Stack>
            )}

            {showModal && !waitingForStory && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleWaitForStory}
                fullWidth
              >
                Wait for Story to Complete
              </Button>
            )}
          </Stack>
        </Container>
      </Box>

      <StoryGeneratingModal
        opened={showModal}
        onClose={() => setShowModal(false)}
        onCreateAnother={handleCreateAnother}
        onGoToLibrary={handleGoToLibrary}
        themeName={selectedThemeData?.label}
        isWaiting={waitingForStory}
      />
    </>
  );
}
